import { useState, type ReactNode } from 'react';
import { Check, ChevronLeft, ChevronRight, Copy, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LocalizedText } from '@iptv/shared-types';
import { cn } from '../cn.js';
import { Button, Field, Input, Textarea } from './primitives.js';
import { EmptyState } from './feedback.js';
import { useMediaQuery } from './hooks.js';

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex animate-fade flex-wrap items-end justify-between gap-x-6 gap-y-4 sm:mb-8">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold text-fg ltr:tracking-tight sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1.5 max-w-3xl text-sm text-muted sm:text-base">{subtitle}</p>}
      </div>
      {actions && (
        <div className="flex flex-wrap gap-2 max-sm:w-full max-sm:*:flex-1">{actions}</div>
      )}
    </div>
  );
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  /**
   * Role in the phone card layout. Defaults: first column → title, key "actions" → actions,
   * everything else → a labeled row.
   */
  mobile?: 'title' | 'row' | 'actions' | 'hidden';
}

/**
 * Responsive data view: a real <table> on wide screens, a stack of cards on phones (and on
 * tablets for wide tables) so nothing scrolls sideways and no text is cut. `mobileCard` replaces
 * the generic labeled card with a page's own compact one; those cards sit two abreast on tablets.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  mobileCard,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  mobileCard?: (row: T) => ReactNode;
}) {
  const { t } = useTranslation();
  const wide = columns.length > 5;
  const compact = useMediaQuery(wide ? '(max-width: 79.99rem)' : '(max-width: 47.99rem)');
  if (rows.length === 0) return <EmptyState message={empty ?? t('common.empty')} />;

  if (compact && mobileCard) {
    return (
      <ul role="list" className="grid gap-2 md:grid-cols-2">
        {rows.map((row) => (
          <li
            key={rowKey(row)}
            className="animate-fade rounded-lg bg-[var(--control-bg)] p-3 shadow-[inset_0_0_0_1px_var(--ui-line)]"
          >
            {mobileCard(row)}
          </li>
        ))}
      </ul>
    );
  }

  if (compact) {
    const role = (c: Column<T>, i: number) =>
      c.mobile ?? (c.key === 'actions' ? 'actions' : i === 0 ? 'title' : 'row');
    return (
      <ul role="list" className="space-y-2.5">
        {rows.map((row) => {
          const title = columns.filter((c, i) => role(c, i) === 'title');
          const fields = columns.filter((c, i) => role(c, i) === 'row');
          const actions = columns.filter((c, i) => role(c, i) === 'actions');
          return (
            <li
              key={rowKey(row)}
              className="animate-fade rounded-lg bg-[var(--control-bg)] p-4 shadow-[inset_0_0_0_1px_var(--ui-line)]"
            >
              {title.map((c) => (
                <div key={c.key} className="min-w-0 text-base font-medium break-words">
                  {c.cell(row)}
                </div>
              ))}
              {fields.length > 0 && (
                <dl className={cn('grid gap-2.5 text-sm', title.length > 0 && 'mt-3')}>
                  {fields.map((c) => (
                    <div key={c.key} className="flex items-start justify-between gap-4">
                      <dt className="shrink-0 text-muted">{c.header}</dt>
                      <dd className="min-w-0 text-end break-words">{c.cell(row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {actions.map((c) => (
                <div
                  key={c.key}
                  className="mt-3 flex justify-end border-t border-line pt-2 [&>div]:flex-wrap"
                >
                  {c.cell(row)}
                </div>
              ))}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="relative -mx-5 overflow-x-auto sm:-mx-6">
      <table className="w-full min-w-[640px] text-sm">
        <thead>
          <tr className="border-b border-line">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn(
                  'px-3 pb-3 text-start text-xs font-medium whitespace-nowrap text-muted first:ps-5 last:pe-5 sm:first:ps-6 sm:last:pe-6',
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="border-b border-line transition-colors duration-[var(--dur-1)] last:border-0 hover:bg-[var(--control-bg)]"
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-3 py-3.5 align-middle first:ps-5 last:pe-5 sm:first:ps-6 sm:last:pe-6',
                    c.className,
                  )}
                >
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (p: number) => void;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <nav
      className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-muted"
      aria-label="pagination"
    >
      <span className="tabular-nums">{t('common.totalItems', { count: total })}</span>
      <div className="flex items-center gap-1 rounded-full bg-[var(--control-bg)] p-1 shadow-[inset_0_0_0_1px_var(--ui-line)]">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label={t('common.previous')}
        >
          <ChevronLeft className="size-4 rtl:rotate-180" />
        </Button>
        <span className="min-w-20 px-2 text-center font-medium text-fg tabular-nums">
          {t('common.pageOf', { page, pages })}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          aria-label={t('common.next')}
        >
          <ChevronRight className="size-4 rtl:rotate-180" />
        </Button>
      </div>
    </nav>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone,
  icon: Icon,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'danger' | 'success';
  icon?: LucideIcon;
}) {
  return (
    <div className="material relative min-w-0 animate-rise rounded-card p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 text-sm text-muted">{label}</div>
        {Icon && (
          <span
            className={cn(
              'grid size-8 shrink-0 place-items-center rounded-[10px]',
              tone === 'danger'
                ? 'bg-danger-soft text-danger'
                : tone === 'success'
                  ? 'bg-success-soft text-success'
                  : 'bg-brand-soft text-brand',
            )}
          >
            <Icon className="size-4" aria-hidden />
          </span>
        )}
      </div>
      <div
        className={cn(
          'mt-2 text-3xl font-semibold tabular-nums ltr:tracking-tight',
          tone === 'danger' && 'text-danger',
          tone === 'success' && 'text-success',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-1.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}

export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={
        done ? <Check className="size-4 text-success" /> : <Copy className="size-4" aria-hidden />
      }
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      <span aria-live="polite">{done ? t('common.copied') : (label ?? t('common.copy'))}</span>
    </Button>
  );
}

/** Large monospace code chip (store codes). Stays LTR inside RTL text. */
export function CodeDisplay({ code, size = 'lg' }: { code: ReactNode; size?: 'md' | 'lg' | 'xl' }) {
  return (
    <span
      className={cn(
        'ltr-iso inline-flex items-center rounded-lg bg-[var(--control-bg)] font-mono font-semibold shadow-[inset_0_0_0_1px_var(--ui-line)] select-all',
        size === 'xl'
          ? 'px-4 py-2 text-2xl tracking-[0.18em] sm:text-3xl'
          : size === 'lg'
            ? 'px-3.5 py-1.5 text-xl tracking-[0.15em] sm:text-2xl'
            : 'px-3 py-1 text-lg tracking-[0.12em]',
      )}
    >
      {code}
    </span>
  );
}

/** Arabic + English inputs for a LocalizedText value. */
export function LocalizedTextFields({
  label,
  value,
  onChange,
  multiline,
  maxLength = 2000,
}: {
  label: ReactNode;
  value: LocalizedText | undefined;
  onChange: (v: LocalizedText) => void;
  multiline?: boolean;
  maxLength?: number;
}) {
  const { t } = useTranslation();
  const v = value ?? {};
  const set = (lang: 'ar' | 'en', text: string) =>
    onChange({ ...v, [lang]: text === '' ? undefined : text });
  const Control = multiline ? Textarea : Input;
  return (
    <fieldset className="min-w-0 space-y-2">
      <legend className="mb-2 text-sm font-semibold">{label}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={<span className="text-xs text-muted">{t('common.arabicText')}</span>}>
          {(p) => (
            <Control
              {...p}
              dir="rtl"
              lang="ar"
              maxLength={maxLength}
              value={v.ar ?? ''}
              onChange={(e) => set('ar', e.target.value)}
            />
          )}
        </Field>
        <Field label={<span className="text-xs text-muted">{t('common.englishText')}</span>}>
          {(p) => (
            <Control
              {...p}
              dir="ltr"
              lang="en"
              maxLength={maxLength}
              value={v.en ?? ''}
              onChange={(e) => set('en', e.target.value)}
            />
          )}
        </Field>
      </div>
    </fieldset>
  );
}
